import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import {
  NOMENCLATURE_POST_CREATE_ITEM,
  NOMENCLATURE_DELETE_DELETE_ITEM,
  NOMENCLATURE_DELETE_DELETE_ITEM_LANGUAGE,
  NOM_GET_GET_VALUES_AUTO_COMPLETE,
  NOMENCLATURE_POST_GET_ITEM,
  NOM_GET_MATTERMOST_CHANNELS,
  NOM_GET_MATTERMOST_SERVERS,
  NOM_GET_LLM_MODELS,
  NOM_GET_LLM_PROMPT_TEMPLATES,
  NOM_GET_NOMENCLATURE,
  NOM_GET_NOMENCLATURE_AUTOCOMPLETE,
  NOM_GET_NOMENCLATURE_BY_ID,
  NOM_GET_LOCATION,
  NOM_GET_GET_PANE_INFO,
  NOM_GET_WORKFLOWS,
  NOMENCLATURE_POST_LIST_ITEMS,
  NOMENCLATURE_POST_LIST_NOMENCLATURES,
  NOMENCLATURE_POST_UPDATE_ITEM,
} from '../endpoints/nomenclature.endpoints';

import { NomenclatureItem } from 'src/app/core/models/core.models';
import {
  AdminConnectorObjectType,
  AdminConnectorPermissionType,
} from 'src/app/admin/models';
import { toQueryParams } from 'src/app/api/api-utils';

import { HttpResponse } from '@angular/common/http';
import { map, Observable } from 'rxjs';

export interface ListNomenclaturesPayload {
  nomenclature_uri: string;
  language_id: number;
}

export interface ListNomenclatureItemsPayload {
  nomenclature_uri: string;
  language_id: number;
}

export interface GetNomenclatureItemPayload {
  nomenclature_uri: string;
  id: number | string;
  language_id: number;
}

export interface CreateNomenclatureItemPayload {
  nomenclature_uri: string;
  main_data: any;
  translations: any;
}

export interface UpdateNomenclatureItemPayload {
  nomenclature_uri: string;
  id: number | string;
  main_data?: any;
  language_id?: number;
  translation_data?: any;
}

export interface DeleteNomenclatureItemPayload {
  nomenclature_uri: string;
  id: number | string;
}

export interface DeleteNomenclatureItemLanguagePayload {
  nomenclature_uri: string;
  id: number | string;
  languageId: number;
}

export interface LlmPromptTemplate {
  id: number;
  role: string;
  name: string;
  description: string;
  /** the prompt body, shown in the read-only preview */
  template: string;
  display_order: number;
  llm_model_id: number;
  /** the model this template runs on, matchable against LlmModel.vendor_model_id */
  vendor_model_id: string;
}

export interface LlmModel {
  id: number;
  vendor_id: number;
  /** the id the vendor's API accepts - this is what gets sent back as a model override */
  vendor_model_id: string;
  /** display name, ready to render */
  name: string;
  /** often empty - render the row only when it has content */
  description: string;
  display_order: number;
  context_window: number;
  default_completion_cap: number;
  long_context_threshold: number;
  price_input: number;
  price_input_cached: number;
  price_output: number;
  price_currency_code: string;
  /** unix seconds; set once the vendor announces a retirement date, null otherwise */
  time_available_to: number | null;
}

// TODO: We will pass typized objects to the services containing all inputs
@Injectable({
  providedIn: 'root',
})
export class NomenclatureApiService {
  private readonly http = inject(HttpClient);

  getAutocompleteResults(
    searchTerm: string,
    propertyUri: string,
  ): Observable<Array<any>> {
    const params = {
      property_uri: propertyUri,
      search_term: searchTerm,
    };

    return this.http.get<any[]>(NOM_GET_GET_VALUES_AUTO_COMPLETE, { params });
  }

  getWorkflows(): Observable<Array<any>> {
    return this.http.get<Array<any>>(NOM_GET_WORKFLOWS);
  }

  /**
   * One nomenclature by name (GET /nom/nomenclature). The response is an object keyed
   * by id, reshaped here into `{id, title}` rows.
   */
  getNomenclatureRecords(name: string): Observable<Array<NomenclatureItem>> {
    const params = toQueryParams({ name });

    return this.http
      .get<{ [key: string]: any }>(NOM_GET_NOMENCLATURE, { params })
      .pipe(
        map((response) =>
          Object.values(response).map((item) => ({
            id: item.id,
            title: item.name,
          })),
        ),
      );
  }

  /**
   * One nomenclature by name, as the raw array the backend returns for
   * list-shaped nomenclatures (e.g. 'period_type', 'rule_api_endpoint') - see
   * `getNomenclatureRecordsRaw` for the object-keyed-by-id variant.
   */
  getNomenclatureList(name: string): Observable<any[]> {
    const params = toQueryParams({ name });
    return this.http.get<any[]>(NOM_GET_NOMENCLATURE, { params });
  }

  getPaneInfo(object: {
    id: string;
    object_type_id: number;
    object_class_id: number;
  }): Observable<{ html: string }> {
    const params = new HttpParams()
      .set('object_id', object.id)
      .set('object_type_id', String(object.object_type_id))
      .set('object_class_id', String(object.object_class_id));
    return this.http.get<{ html: string }>(NOM_GET_GET_PANE_INFO, { params });
  }

  getConnectorObjectTypes(): Observable<AdminConnectorObjectType[]> {
    const params = toQueryParams({ name: 'connector_object_type' });
    return this.http.get<AdminConnectorObjectType[]>(NOM_GET_NOMENCLATURE, {
      params,
    });
  }

  getConnectorPermissionTypes(): Observable<AdminConnectorPermissionType[]> {
    const params = toQueryParams({ name: 'connector_permission_type' });
    return this.http.get<AdminConnectorPermissionType[]>(NOM_GET_NOMENCLATURE, {
      params,
    });
  }

  getMattermostChannels(serverId: number): Observable<Array<any>> {
    let params: HttpParams = new HttpParams();
    if (serverId) {
      params = params.set('serverId', serverId.toString());
    }

    return this.http.get<Array<NomenclatureItem>>(NOM_GET_MATTERMOST_CHANNELS, {
      params,
    });
  }

  getMattermostServers(): Observable<Array<NomenclatureItem>> {
    return this.http.get<Array<NomenclatureItem>>(NOM_GET_MATTERMOST_SERVERS);
  }

  /**
   * The models a caller may select, already ordered for display.
   */
  getLlmModels(): Observable<Array<LlmModel>> {
    return this.http.get<Array<LlmModel>>(NOM_GET_LLM_MODELS);
  }

  /**
   * The prompt templates a caller may select, already filtered to active ones
   * and ordered for display.
   *
   * @param role omit for the backend default ('autostory')
   */
  getLlmPromptTemplates(role?: string): Observable<Array<LlmPromptTemplate>> {
    const params = toQueryParams({ role });

    return this.http.get<Array<LlmPromptTemplate>>(
      NOM_GET_LLM_PROMPT_TEMPLATES,
      {
        params,
      },
    );
  }

  /**
   * One nomenclature by name, as the raw object keyed by id - for callers that
   * do their own reshaping/caching (see `getNomenclatureRecords` for the
   * `{id, title}` rows variant).
   */
  getNomenclatureRecordsRaw(
    name: string,
  ): Observable<{ [key: string]: NomenclatureItem | any }> {
    const params = toQueryParams({ name });

    return this.http.get<{ [key: string]: NomenclatureItem | any }>(
      NOM_GET_NOMENCLATURE,
      { params },
    );
  }

  /**
   * One autocomplete page for a nomenclature. `observe: 'response'` so callers
   * can also read the paging header.
   */
  getNomenclatureAutocomplete(
    nomenclature: string,
    term: string,
    opts?: { pageToken?: string; limit?: number },
  ): Observable<HttpResponse<{ items?: any[]; next_page_token?: string } | any>> {
    let params = new HttpParams()
      .set('nomenclature', nomenclature)
      .set('term', term ?? '');

    if (opts?.pageToken) params = params.set('pageToken', opts.pageToken);
    if (opts?.limit != null) params = params.set('limit', String(opts.limit));

    return this.http.get<{ items?: any[]; next_page_token?: string } | any>(
      NOM_GET_NOMENCLATURE_AUTOCOMPLETE,
      { params, observe: 'response' },
    );
  }

  /** Nomenclature items by their ids, as the raw object keyed by id. */
  getNomenclatureItemsByIds(
    nomenclature: string,
    ids: number[],
  ): Observable<{ [key: string]: NomenclatureItem | any }> {
    const params = toQueryParams({ nomenclature, 'ids[]': ids });

    return this.http.get<{ [key: string]: NomenclatureItem | any }>(
      NOM_GET_NOMENCLATURE_BY_ID,
      { params },
    );
  }

  getLocation(
    locationId: number,
  ): Observable<{ id: number; name: string } | null> {
    const params = new HttpParams().set('locationId', locationId.toString());

    return this.http.get<{ id: number; name: string } | null>(
      NOM_GET_LOCATION,
      { params },
    );
  }

  listNomenclatures(payload: ListNomenclaturesPayload): Observable<any> {
    return this.http.post<any>(NOMENCLATURE_POST_LIST_NOMENCLATURES, payload);
  }

  /**
   * List nomenclatures for a given nomenclatureName and languageId.
   * Returns data formatted for ag‑Grid (rows and lastRow).
   */
  listItems(payload: ListNomenclatureItemsPayload): Observable<any> {
    return this.http.post<any>(NOMENCLATURE_POST_LIST_ITEMS, payload);
  }

  getItem(payload: GetNomenclatureItemPayload): Observable<any> {
    return this.http.post<any>(NOMENCLATURE_POST_GET_ITEM, payload);
  }

  /**
   * Creates a new nomenclature item along with its translations.
   */
  createItem(payload: CreateNomenclatureItemPayload): Observable<any> {
    return this.http.post<any>(NOMENCLATURE_POST_CREATE_ITEM, payload);
  }

  /**
   * Updates an existing nomenclature item and/or its translation.
   */
  updateItem(payload: UpdateNomenclatureItemPayload): Observable<any> {
    return this.http.post<any>(NOMENCLATURE_POST_UPDATE_ITEM, payload);
  }

  /**
   * Deletes a nomenclature item and all its translations.
   * Note: Using http.request() for DELETE with a body.
   */
  deleteItem(payload: DeleteNomenclatureItemPayload): Observable<any> {
    return this.http.request<any>('delete', NOMENCLATURE_DELETE_DELETE_ITEM, {
      body: payload,
    });
  }

  /**
   * Deletes a single language translation for a nomenclature item.
   * Note: Using http.request() for DELETE with a body.
   */
  deleteItemLanguage(
    payload: DeleteNomenclatureItemLanguagePayload,
  ): Observable<any> {
    return this.http.request<any>(
      'delete',
      NOMENCLATURE_DELETE_DELETE_ITEM_LANGUAGE,
      {
        body: payload,
      },
    );
  }
}
